import {
  BUILDING_HEADCOUNT_SEATCOUNT,
  BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS,
  FLOOR_HEADCOUNT_SEATCOUNT,
  Growth,
} from 'spw-redux/modules/calculators/types';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentScenario } from 'spw-redux/modules/calculators/selectors';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  setHeadcountSeatCountBase as setHeadcountSeatCountBaseAction,
  setHeadcountSeatCountGrowthRate as setHeadcountSeatCountGrowthRateAction,
  setSelectedYear,
  setYearHeadcount,
} from 'spw-redux/modules/calculators/actions';
import {
  MAX_GROWTH_TABLE_HC_INPUT,
  MAX_GROWTH_TABLE_RATE_INPUT,
  MIN_GROWTH_TABLE_RATE_INPUT,
  NO_DATA_TO_SHOW,
} from 'spw-constants';
import { convertValueToDecimal } from 'spw-components/growth-calculator/utils';
import { limitInputValue } from 'spw-components/worksheet/utils';
import { InputAdornment, TableRow } from '@mui/material';
import {
  BodyCell,
  CalculatorValue,
  GrowthRateCell,
  GrowthTableHeadcountInput,
  HeadcountInput,
  RowSmall,
  RowTitle,
  RowTitleDepartment,
} from 'spw-components/growth-calculator/styles';
import {
  asUsfOrZero,
  formatBigNumber,
  handleOnKeyPress,
} from 'spw-shared/utils';
import { range } from 'ramda';
import { isTotalProgram } from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';

const TitleColumn = ({
  primary,
  secondary,
}: {
  primary: string;
  secondary?: string;
}) => {
  return (
    <>
      <RowTitleDepartment>{primary}</RowTitleDepartment>
      {secondary && <RowSmall>{secondary}</RowSmall>}
    </>
  );
};

const GrowthEntries = ({
  growth,
  isLinked,
  testingIdPrefix,
  disabledInputs,
}: {
  growth: Growth;
  isLinked?: boolean;
  testingIdPrefix: string;
  disabledInputs: boolean;
}) => {
  const dispatch = useDispatch();
  const currentScenario = useSelector(selectCurrentScenario);
  const project = useSelector(currentProject);
  const building = project.buildings?.[0];
  const { department } = growth;
  const { startYear } = currentScenario;

  const [
    headcountSeatCountGrowthRate,
    setHeadcountSeatCountGrowthRate,
  ] = useState(growth.headcountSeatCountGrowthRate?.toFixed(1));
  const [headcountSeatCountArray, setHeadcountSeatCountArray] = useState(
    growth.growthEntries.map((g) => g.value)
  );

  const [headcountSeatCountBase, setHeadcountSeatCountBase] = useState(
    growth.headcountSeatCountBase?.toString()
  );

  const isDirty = useMemo(() => {
    if (currentScenario?.id) return true;

    if (growth.headcountSeatCountGrowthRate) return true;

    if (growth.growthEntries.some((ge) => ge.value)) return true;

    if (growth.headcountSeatCountBase) return true;

    return false;
  }, [
    currentScenario?.id,
    growth.headcountSeatCountGrowthRate,
    growth.growthEntries,
    growth.headcountSeatCountBase,
  ]);

  useEffect(() => {
    setHeadcountSeatCountBase(growth.headcountSeatCountBase?.toString());
  }, [setHeadcountSeatCountBase, growth.headcountSeatCountBase]);

  useEffect(() => {
    setHeadcountSeatCountGrowthRate(
      growth.headcountSeatCountGrowthRate?.toFixed(1)
    );
  }, [growth.headcountSeatCountGrowthRate]);

  useEffect(() => {
    setHeadcountSeatCountArray(growth.growthEntries.map((g) => g.value));
  }, [setHeadcountSeatCountArray, growth.growthEntries]);

  const handleGrowthRateInputClicked = useCallback(() => {
    setHeadcountSeatCountGrowthRate(
      growth.headcountSeatCountGrowthRate
        ? growth.headcountSeatCountGrowthRate.toFixed(1)
        : ''
    );
  }, [growth.headcountSeatCountGrowthRate]);

  const handleGrowthRateInputOnBlur = useCallback(() => {
    if (headcountSeatCountGrowthRate === '') {
      setHeadcountSeatCountGrowthRate(undefined);
      dispatch(setHeadcountSeatCountGrowthRateAction(undefined));
      return;
    }

    let value = Number(headcountSeatCountGrowthRate);

    if (Number.isNaN(value)) {
      setHeadcountSeatCountGrowthRate(undefined);
      dispatch(setHeadcountSeatCountGrowthRateAction(undefined));
      return;
    }

    if (!headcountSeatCountGrowthRate) {
      dispatch(setHeadcountSeatCountGrowthRateAction(0));
      setHeadcountSeatCountGrowthRate('0.0');
    } else {
      value = Math.min(
        MAX_GROWTH_TABLE_RATE_INPUT,
        Math.max(MIN_GROWTH_TABLE_RATE_INPUT, value)
      );
      dispatch(setHeadcountSeatCountGrowthRateAction(value));
      setHeadcountSeatCountGrowthRate(value.toFixed(1));
    }
  }, [headcountSeatCountGrowthRate, dispatch]);

  const handleGrowthRateInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setHeadcountSeatCountGrowthRate(
        convertValueToDecimal(event.target.value)
      );
    },
    []
  );

  const handleBaseInputClicked = useCallback(() => {
    setHeadcountSeatCountBase(
      growth.headcountSeatCountBase
        ? growth.headcountSeatCountBase.toString()
        : ''
    );
  }, [setHeadcountSeatCountBase, growth.headcountSeatCountBase]);

  const handleBaseInputOnBlur = useCallback(() => {
    let value = parseInt(headcountSeatCountBase || '0', 10);
    const isClean = value === growth.headcountSeatCountBase;

    if (isClean) {
      setHeadcountSeatCountBase(value.toString());
      return;
    }

    if (!headcountSeatCountBase) {
      dispatch(setHeadcountSeatCountBaseAction(0));
      setHeadcountSeatCountBase('0');
    } else {
      value = Math.max(0, value);
      dispatch(setHeadcountSeatCountBaseAction(value));
      setHeadcountSeatCountBase(value.toString());
    }
  }, [
    headcountSeatCountBase,
    growth.headcountSeatCountBase,
    setHeadcountSeatCountBase,
    dispatch,
  ]);

  const handleBaseInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setHeadcountSeatCountBase(
        limitInputValue(event, MAX_GROWTH_TABLE_HC_INPUT)
      );
    },
    [setHeadcountSeatCountBase]
  );

  const handleYearInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      event.persist();

      setHeadcountSeatCountArray((prevValue) => {
        const newValue = [...prevValue];
        newValue[index] = parseInt(event.target.value || '0', 10);

        return newValue;
      });
    },
    []
  );

  const handleYearInputBlur = useCallback(
    (headcountIndex: number) => {
      const headcount = headcountSeatCountArray[headcountIndex];
      const otherEntriesAreClean = headcountSeatCountArray
        .filter((_, i) => i !== headcountIndex)
        .every((h) => !h);
      const thisOneIsDirty =
        headcount !== growth.growthEntries[headcountIndex].value;

      if (thisOneIsDirty) {
        dispatch(
          setYearHeadcount({
            headcount,
            headcountIndex,
            otherEntriesAreClean,
          })
        );
      }
    },
    [headcountSeatCountArray, growth.growthEntries, dispatch]
  );

  const _isTotalProgram = isTotalProgram(currentScenario);

  const titleColumn = useMemo(() => {
    if (_isTotalProgram && growth.aggregation)
      return (
        <>
          <RowTitle>All Departments</RowTitle>
          <RowSmall>Total Headcount</RowSmall>
        </>
      );

    let titleColumnProps;
    switch (currentScenario.calculatorType) {
      case BUILDING_HEADCOUNT_SEATCOUNT: {
        titleColumnProps = {
          primary: 'Building',
        };
        break;
      }

      case FLOOR_HEADCOUNT_SEATCOUNT: {
        const floorId = currentScenario.floorId || building?.floors[0].id;

        titleColumnProps = {
          primary: building?.floors.find((f) => f.id === floorId)?.name,
        };
        break;
      }

      default: {
        titleColumnProps = {
          primary: department.name,
          secondary: department.secondaryIdentifier,
        };
        break;
      }
    }

    return <TitleColumn {...titleColumnProps} />;
  }, [
    _isTotalProgram,
    growth.aggregation,
    currentScenario.calculatorType,
    department?.name,
    department?.secondaryIdentifier,
    building?.floors,
    currentScenario.floorId,
  ]);

  return (
    <TableRow>
      <BodyCell
        data-cy={`${testingIdPrefix}_growth_label_${
          department ? department?.id : 'aggregation'
        }`}
      >
        {titleColumn}
      </BodyCell>
      <GrowthRateCell className="growth" align="center">
        {_isTotalProgram && !growth.department?.id ? (
          <div />
        ) : (
          <HeadcountInput
            data-testid="hcsc_growth_rate"
            data-cy={`${testingIdPrefix}_hcsc_growth_rate_${
              department ? department.id : 'agg'
            }`}
            value={headcountSeatCountGrowthRate ?? NO_DATA_TO_SHOW}
            disabled={disabledInputs}
            onKeyPress={handleOnKeyPress}
            onFocus={handleGrowthRateInputClicked}
            onBlur={handleGrowthRateInputOnBlur}
            onChange={handleGrowthRateInputChange}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            className="with-end-adornment no-offset growth-rate"
          />
        )}
      </GrowthRateCell>
      <BodyCell
        align="center"
        className={
          startYear === currentScenario.targetYear
            ? 'calculator-selected-year'
            : ''
        }
      >
        {isTotalProgram(currentScenario) && !growth.department?.id ? (
          <CalculatorValue
            className={`current ${growth.aggregation ? 'total' : ''}`}
            data-cy={`${testingIdPrefix}_hcsc_base_total`}
          >
            {asUsfOrZero(growth.headcountSeatCountBase)}
          </CalculatorValue>
        ) : (
          <GrowthTableHeadcountInput
            data-testid={`${testingIdPrefix}_hcsc_base_${
              department ? department.id : 'agg'
            }`}
            data-cy={`${testingIdPrefix}_hcsc_base_${
              department ? department.id : 'agg'
            }`}
            disabled={disabledInputs}
            value={formatBigNumber(headcountSeatCountBase)}
            onBlur={handleBaseInputOnBlur}
            onFocus={handleBaseInputClicked}
            onKeyPress={handleOnKeyPress}
            onChange={handleBaseInputChange}
            className={!isDirty ? 'highlight' : ''}
          />
        )}
      </BodyCell>
      {range(currentScenario.startYear + 1, currentScenario.startYear + 11).map(
        (year: number, index: number) => {
          if (index + 1 <= growth.growthEntries.length) {
            const growthEntry = growth.growthEntries[index];

            const testId = `${testingIdPrefix}_value_${
              growth.aggregation ? `agg_${growthEntry.year}` : growthEntry.year
            }`;
            return (
              <BodyCell
                key={`growth-entry-${year}`}
                className={[
                  growthEntry.year === currentScenario.targetYear &&
                  (growth.aggregation ||
                    currentScenario.calculatorType ===
                      BUILDING_HEADCOUNT_SEATCOUNT_DEPARTMENTS)
                    ? 'calculator-selected-year'
                    : '',
                  'calculator-value',
                ].join(' ')}
                onClick={() => {
                  if (!disabledInputs && !isLinked) {
                    dispatch(setSelectedYear(growthEntry.year));
                  }
                }}
                align="center"
              >
                {isTotalProgram(currentScenario) && !growth.department?.id ? (
                  <CalculatorValue
                    className={`${growth.aggregation ? 'total' : ''}`}
                    data-testid={testId}
                    data-cy={testId}
                  >
                    {asUsfOrZero(growthEntry.value, false)}
                  </CalculatorValue>
                ) : (
                  <GrowthTableHeadcountInput
                    data-testid={testId}
                    data-cy={testId}
                    disabled={disabledInputs}
                    value={asUsfOrZero(headcountSeatCountArray[index], false)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleYearInputChange(e, index)
                    }
                    onBlur={() => handleYearInputBlur(index)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </BodyCell>
            );
          }
          return <BodyCell key={`${year}value`} />;
        }
      )}
    </TableRow>
  );
};
export default GrowthEntries;
